import { getReturnIdsFromDatasetInstruction } from 'helpers/dataset.helper/dataset.helper'
import {
  applyToNestedKeysThatStartWith,
  applyToNestedString,
  applyToNestedStringForDictKeys,
  clone,
  isObject,
  merge,
} from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import authStore from 'store/auth/auth'
import elplanStore from 'store/elplan/elplan'
import DEFAULT_ALIASES, { DEFAULT_CLIENT_SIDE_ALIASES, OPTJOB_TYPE_ID_TO_NAME } from 'ui/uiConfig/template/aliases'
import DEFAULT_DATASET_TEMPLATES from 'ui/uiConfig/template/datasets'
import { prefixReturnIdsForDbSDatasetInstruction } from 'utils/dbs/dbs.helper'
import { systemFeatureFlagActive } from 'utils/systemUtils'
import { snapshot } from 'valtio'

export function getUiConfig(uiConfigs: UiConfigCollection, uid: number): UiConfig {
  return uiConfigs[uid]
}

export function getUiConfigById(uiConfigs: UiConfigCollection, id: number): UiConfig | null {
  for (const uid in uiConfigs) {
    if (uiConfigs[uid].id === id) {
      return uiConfigs[uid]
    }
  }

  return null
}

export function getUiconfigUidFromId(uiConfigs: UiConfigCollection, id: number): number | undefined | null {
  for (const uid in uiConfigs) {
    if (uiConfigs[uid].id === id) {
      return uiConfigs[uid].uid
    }
  }

  return null
}

function getLatestBidDeadlineDateTime(deadlineTime: `${number}:${number}:${number}`): ISODateTime {
  const todaysDeadline = new Date()
  const now = new Date()

  const [hours, minutes, seconds] = deadlineTime.split(':').map(Number)
  todaysDeadline.setHours(hours, minutes, seconds, 0)

  if (now < todaysDeadline) {
    // Return the same time but one day earlier if now is earlier than today's deadline
    const adjustedDate = new Date(todaysDeadline)
    adjustedDate.setDate(todaysDeadline.getDate() - 1)
    return adjustedDate.toISOString() as ISODateTime
  } else {
    // Return today's deadline if now is later than today's deadline
    return todaysDeadline.toISOString() as ISODateTime
  }
}

export function getAllAliases(
  aliases: Array<UiConfigAliases | undefined>,
  options: {
    clientSideAliases?: boolean
  } = {}
): UiConfigAliases {
  const authSnap = snapshot(authStore)
  const elplanSnap = snapshot(elplanStore)
  const activeSystem = authSnap.activeSystem

  const dynamicDefaultAliases: UiConfigAliases = {
    system_id: activeSystem?.id || 0,
    system: activeSystem?.name || '',
    opt_model_id: activeSystem?.primary_opt_model?.id || 0,
    bid_deadline_time: getLatestBidDeadlineDateTime(elplanSnap.brokerDeadlineTime),
    currency: activeSystem?.currency?.toLocaleLowerCase() || 'sek',
    organization: activeSystem?.organization_name || '',
    el_bidding_area: activeSystem?.el_bidding_area_name || '',
  }

  let allAliases: UiConfigAliases = {
    ...(options?.clientSideAliases !== false ? DEFAULT_CLIENT_SIDE_ALIASES : {}),
    ...DEFAULT_ALIASES,
    ...dynamicDefaultAliases,
  }

  aliases
    .filter((alias) => !!alias && isObject(alias))
    .forEach((alias) => {
      allAliases = {
        ...allAliases,
        ...alias,
      }
    })

  // Dynamic fall-back aliases if not already present
  if (!allAliases.optjob_type && allAliases.opt_job_type_id !== undefined) {
    allAliases.optjob_type = OPTJOB_TYPE_ID_TO_NAME[allAliases.opt_job_type_id as number] || ``
  }

  // Weather forecast
  if (allAliases.weather_forecast_locality && !allAliases.weather_forecast_organization) {
    allAliases.weather_forecast_organization = String(allAliases.weather_forecast_locality)
      .replace('forecast_weather_', '')
      .split('_')[0]
    allAliases.weather_forecast_system = String(allAliases.weather_forecast_locality)
      .replace('forecast_weather_', '')
      .split('_')[1]
    allAliases.weather_forecast_percentile = 50
  }

  // Heatload forecast
  if (allAliases.heatload_forecast_locality && !allAliases.heatload_forecast_organization) {
    allAliases.heatload_forecast_organization = String(allAliases.heatload_forecast_locality)
      .replace('forecast_heat_', '')
      .split('_')[0]
    allAliases.heatload_forecast_system = String(allAliases.heatload_forecast_locality)
      .replace('forecast_heat_', '')
      .split('_')[1]
    allAliases.heatload_forecast_percentile = 50

    // Launch of stefan forecasts, see https://github.com/sigholm/abs-forecast-service/tree/main/stefan
    // This makes sure the correct forecast used for optimisations is shown in the /optimize page.
    const forecastStefanEnable = systemFeatureFlagActive(activeSystem, 'forecast_stefan_enable')
    allAliases.heatload_forecast_abs_service = forecastStefanEnable ? 'abs_forecast_stefan' : 'abs_forecast'
  }

  // Heatload outcome
  // This might not be needed anymore, but it's hard to tell, so we keep it for now.
  if (!allAliases.heatload_organization) {
    allAliases.heatload_organization = allAliases.organization
    allAliases.heatload_system = allAliases.system
  }

  // Electriticy prices
  if (!allAliases.electricity_price_internal_id) {
    if (allAliases.electricity_price_locality) {
      const bidding_are = String(allAliases.electricity_price_locality)
        .replace('forecast_electricity_price_', '')
        .split('_')[0]
      allAliases.electricity_price_internal_id = `spot_price_${bidding_are}`
    } else {
      allAliases.electricity_price_internal_id = `spot_price_${activeSystem?.el_bidding_area_name}`
    }
  }

  return allAliases
}

export function generallyApplyAliases<T>(data: T, aliases?: UiConfigAliases): T {
  const allAliases = getAllAliases([clone(aliases || {})])
  const parsedUiConfig = applyToNestedString<T>(data, (str: string): UiConfigAliasValue => {
    if (str.startsWith(`$`)) {
      const key = str.substring(1, str.length)
      const alias = allAliases[key]

      if (alias === undefined) {
        return str
      }

      return alias
    }

    return str
  })

  return parsedUiConfig
}

export function applyAliases(uiConfig: UiConfig, baseAlias?: UiConfigAliases): UiConfig {
  const allAliases = getAllAliases([uiConfig.alias, baseAlias])

  let parsedUiConfig = applyToNestedString<UiConfig>(uiConfig, (str: string): UiConfigAliasValue => {
    const aliasPattern = /\{\$(.*?)\}([^{]*)?/g // Regular expression to match {$...} and optionally any characters that are not whitespace or a slash
    if (aliasPattern) {
      // Takes the $alias wrapped in {} and returns the alias and the rest of the string
      str = str.replace(aliasPattern, (match, key, rest = '') => {
        const alias = allAliases[key]
        if (alias === undefined || Array.isArray(alias)) {
          return match
        }

        return `${alias}${rest}`
      })
    }

    if (str.startsWith(`$`)) {
      const key = str.substring(1, str.length)
      const alias = allAliases[key]

      if (alias === undefined) {
        return str
      }

      return alias
    }

    if (str.startsWith(`__`)) {
      const key = str.substring(2, str.length)
      const translation = i18n.t(key)

      return translation ?? str
    }
    return str
  })

  parsedUiConfig = applyToNestedKeysThatStartWith(parsedUiConfig, '$', (str: string) => {
    const key = str.substring(1, str.length)
    const alias = allAliases[key]

    if (alias === undefined || Array.isArray(alias)) {
      return str
    }

    return String(alias)
  })

  return parsedUiConfig
}

export function mergeUiConfigs(uiConfig: UiConfig, uiConfigOverriding: Partial<UiConfig>): UiConfig {
  return merge(clone(uiConfig), clone(uiConfigOverriding)) as UiConfig
}

/**
 * Applies a previewCollection entry onto a UiConfig.
 *
 * Editor drafts written via `setPreviewDraft` always carry an explicit
 * `display_name` key (even when the value is undefined), which serves as the
 * discriminator for full-replacement semantics. In that case a top-level
 * spread is used so that top-level editable fields deleted by the user (e.g.
 * an `alias` block removed from the editor JSON) are correctly omitted in the
 * result rather than being silently preserved from the saved config.
 *
 * Partial previews such as ItemTable/TagsConfigTable updates (which only
 * touch `props.items` or `props.tags_config`) do not carry `display_name` and
 * continue to use deep merge so that unrelated saved fields are preserved.
 */
export function applyPreviewEntry(uiConfig: UiConfig, previewEntry: Partial<UiConfig>): UiConfig {
  if ('display_name' in previewEntry) {
    // Full editor draft: shallow spread at the top level so that editable
    // fields deleted from the editor JSON override (and therefore remove) the
    // corresponding saved field in the result.
    // Avoid JSON-based cloning for previewEntry here because explicit
    // `undefined` keys encode deletions – JSON clone would silently drop them.
    const previewEntryCopy = Object.fromEntries(Object.entries(previewEntry))
    return { ...clone(uiConfig), ...previewEntryCopy } as UiConfig
  }

  // Partial preview (e.g. ItemTable items/tags update): deep merge to keep
  // unrelated saved fields intact.
  return mergeUiConfigs(uiConfig, previewEntry)
}

function applyConditionalBaseItemOverrides(
  baseItems: UiConfigPropsItem[],
  conditionalOverrides: UiConfigProps['base_items_conditional_overrides']
): UiConfigPropsItem[] {
  if (!conditionalOverrides?.length) return baseItems
  return baseItems.map((item) => {
    let overrides: Partial<UiConfigPropsItem> = {}
    for (const condOverride of conditionalOverrides) {
      const matchAttr = isObject(condOverride?.match_attr) ? condOverride.match_attr : {}
      const matchEntries = Object.entries(matchAttr)
      if (!matchEntries.length) continue

      const overrideItem = isObject(condOverride?.item) ? condOverride.item : {}
      const matches = matchEntries.every(([attr, value]) => item[attr] === value)
      if (matches) {
        overrides = { ...overrides, ...overrideItem }
      }
    }
    return Object.keys(overrides).length ? { ...item, ...overrides } : item
  })
}

export function prefixReturnIds<T>(data: T, prefix: string): T {
  if (!prefix) {
    return data
  }

  data = prefixReturnIdsForDbSDatasetInstruction(data, prefix)

  return applyToNestedStringForDictKeys(
    data,
    new Set(['return_id', 'data_id', 'variables']),
    (str) => `${prefix}${str}`
  )
}

/** Shallow: an override key such as `sources` replaces the base contract's value whole. */
function mergeContractOverride(
  instructions: Partial<DatasetInstruction>[],
  contract: Partial<DatasetInstruction>['contract'] | undefined
): Partial<DatasetInstruction>[] {
  if (!contract) {
    return instructions
  }

  return instructions.map(
    (instruction) =>
      ({ ...instruction, contract: { ...instruction.contract, ...contract } }) as Partial<DatasetInstruction>
  )
}

/**
 * BaseConfig's dataset instructions as one `multi_base_items_override` entry runs them: its contract merged
 * in, its aliases applied, and its `return_id_prefix` on every return id so `optimal_` and `outcome_` copies
 * do not collide. A `$alias` the entry leaves unresolved is left for `getAliasesForAliasExpansion`'s pass.
 */
export function expandBaseInstructionsForOverride(
  baseInstructions: Partial<DatasetInstruction>[],
  override: Pick<UiConfigBaseItemOverride, 'alias' | 'dataset_instruction' | 'return_id_prefix'>
): Partial<DatasetInstruction>[] {
  const instructions = mergeContractOverride(clone(baseInstructions), clone(override.dataset_instruction?.contract))

  return prefixReturnIds(generallyApplyAliases(instructions, override.alias || {}), override.return_id_prefix || ``)
}

export function mergeWithBaseUiConfig(uiConfig: UiConfig, baseUiConfig: UiConfig | null): UiConfig {
  const mergedUiConfig = clone(uiConfig)

  if (!baseUiConfig) {
    return mergedUiConfig
  }

  // FIXME: Override-filter applies filter to all datasetinstructions, not only base items. It is an unwanted, since the variable name is "base_items_filter" and gives the impression that filter only applies to base_items.
  function overrideFilter(config: UiConfig): UiConfig {
    if (!config?.props?.base_items_filter) {
      return config
    }

    return {
      ...config,
      dataset_instructions: config.dataset_instructions.map((d) => ({
        ...d,
        filter: {
          ...(d.filter || {}),
          ...config.props.base_items_filter,
        },
      })),
    }
  }

  const baseDatasetInstructions: Record<string, DatasetInstruction> = {}
  ;(baseUiConfig.props?.dataset_instructions as DatasetInstruction[])?.forEach((datasetInstruction) => {
    const returnIds = getReturnIdsFromDatasetInstruction(datasetInstruction)

    returnIds.forEach((returnId) => {
      baseDatasetInstructions[returnId] = clone(datasetInstruction)
    })
  })
  // Template dataset instruction
  mergedUiConfig.dataset_instructions = mergedUiConfig.dataset_instructions.map((instruction) => {
    if (instruction.type !== `template`) {
      return instruction
    }

    const templateInstruction =
      baseDatasetInstructions[instruction.return_id as string] ||
      DEFAULT_DATASET_TEMPLATES[instruction.return_id as string] ||
      {}

    return merge(instruction, templateInstruction)
  })

  // Generate items & datasets based on "base_items"
  if (mergedUiConfig.props?.base_items && baseUiConfig.props?.items) {
    const includeReturnIds: Set<string> = new Set()
    const excludeReturnIds: Set<string> = new Set()

    Object.entries(mergedUiConfig.props.base_items || {}).forEach(([returnId, value]) => {
      if (value === false) {
        excludeReturnIds.add(returnId)
      } else if (returnId !== `all`) {
        includeReturnIds.add(returnId)
      }
    })

    Object.entries(mergedUiConfig.props.include_only_base_items_with_attr || {}).forEach(([attr, value]) => {
      baseUiConfig.props.items?.forEach((item) => {
        if (item[attr] === value) {
          includeReturnIds.add(item.data_id)
        } else {
          excludeReturnIds.add(item.data_id)
        }
      })
    })

    let baseItems: UiConfigPropsItem[] = []
    if (mergedUiConfig.props.base_items?.all === true) {
      baseItems = clone(baseUiConfig.props.items || [])
    } else {
      baseItems = clone(baseUiConfig.props.items?.filter((item) => includeReturnIds.has(item.data_id)) || [])
    }

    baseItems = baseItems.filter((item) => !excludeReturnIds.has(item.data_id))

    if (mergedUiConfig.props.base_items_conditional_overrides?.length) {
      baseItems = applyConditionalBaseItemOverrides(baseItems, mergedUiConfig.props.base_items_conditional_overrides)
    }

    if (mergedUiConfig.props.multi_base_items_override?.length) {
      mergedUiConfig.props.multi_base_items_override.forEach((override) => {
        // If this override has calc_override, generate calc dataset instructions per base item
        // instead of cloning base dataset instructions
        if (override.calc_override) {
          const { operator, source_prefixes } = override.calc_override
          const resultPrefix = override.return_id_prefix || ``

          // Generate items for the computed stack group
          if (Object.keys(override.item || {}).length || override.title_suffix) {
            mergedUiConfig.props.items = [
              ...(mergedUiConfig.props.items || []),
              ...(baseItems || []).map((i) => {
                if (override.title_suffix) {
                  const titleWithSuffix = `${i.title} ${override.title_suffix}`
                  return prefixReturnIds(
                    {
                      ...i,
                      ...override.item,
                      title: titleWithSuffix,
                    },
                    resultPrefix
                  )
                } else {
                  return prefixReturnIds(
                    {
                      ...i,
                      ...override.item,
                    },
                    resultPrefix
                  )
                }
              }),
            ]
          }

          // Generate one calc dataset instruction per base item
          const nullAsZero = override.calc_override.null_as_zero !== false // Default to true, but allow override to set it to false
          const calcInstructions: Partial<DatasetInstruction>[] = baseItems.map((item) => ({
            type: `calc` as const,
            return_id: `${resultPrefix}${item.data_id}`,
            contract: {
              operator,
              variables: source_prefixes.map((prefix) => `${prefix}${item.data_id}`),
              element_wise: true,
              null_as_zero: nullAsZero,
            },
            filter: {},
          }))

          mergedUiConfig.dataset_instructions.push(...calcInstructions)
          return
        }

        if (Object.keys(override.item || {}).length || override.title_suffix) {
          mergedUiConfig.props.items = [
            ...(mergedUiConfig.props.items || []),
            ...(baseItems || []).map((i) => {
              if (override.title_suffix) {
                const titleWithSuffix = `${i.title} ${override.title_suffix}`
                return prefixReturnIds(
                  {
                    ...i,
                    ...override.item,
                    title: titleWithSuffix,
                  },
                  override.return_id_prefix || ``
                )
              } else {
                return prefixReturnIds(
                  {
                    ...i,
                    ...override.item,
                  },
                  override.return_id_prefix || ``
                )
              }
            }),
          ]
        }
        mergedUiConfig.dataset_instructions.unshift(
          ...expandBaseInstructionsForOverride(baseUiConfig.dataset_instructions, override)
        )
      })
    } else if (mergedUiConfig.props.multi_base_item_alias_renaming?.length) {
      mergedUiConfig.props.items = [...baseItems, ...(mergedUiConfig.props?.items || [])]

      mergedUiConfig.props.multi_base_item_alias_renaming.forEach((aliases) => {
        const dataset_instructions = generallyApplyAliases(clone(baseUiConfig.dataset_instructions), aliases)
        mergedUiConfig.dataset_instructions.unshift(...dataset_instructions)
      })
    } else {
      mergedUiConfig.props.items = [...baseItems, ...(mergedUiConfig.props?.items || [])]

      mergedUiConfig.dataset_instructions.unshift(...clone(baseUiConfig.dataset_instructions))
    }

    const filteredUiConfig = overrideFilter(mergedUiConfig)
    mergedUiConfig.dataset_instructions = filteredUiConfig.dataset_instructions
  }

  // Copy base tags_config from baseUiConfig and override with the tags_config of this uiConfig.
  mergedUiConfig.props.tags_config = JSON.parse(
    JSON.stringify({ ...baseUiConfig.props.tags_config, ...mergedUiConfig.props.tags_config })
  )

  return mergedUiConfig
}

function translateUiConfigField(text: string): string {
  if (text.startsWith(`__`)) {
    return i18n.t(text.split(`__`)[1])
  }

  // Translate interpolated strings. Example: "Heat {{system=Demostaden}}" === "Värme Demostaden". Note that "Heat {{system}}" must be a key in the translation files
  const interpolatedString = text.match(/\{\{(.*?)\}\}/g)
  if (interpolatedString) {
    const interpolationParameters: { [key: string]: string } = {}

    interpolatedString.forEach((str) => {
      let key = str.substring(2, str.length - 2)
      if (key.startsWith(`__`)) {
        key = key.substring(2)
      }
      const parameterKey = key.split(`=`)[0]
      const parameterValue = key.split(`=`)[1]
      interpolationParameters[parameterKey] = parameterValue

      text = text.replace(str, `{{${parameterKey}}}`)
    })
    return i18n.t(text, interpolationParameters)
  }
  return i18n.t(text)
}

export function translatedUiConfig(uiConfig: UiConfig): UiConfig {
  return applyToNestedStringForDictKeys(uiConfig, new Set(['title', 'tooltip']), translateUiConfigField) as UiConfig
}

// Restrictive: If the item is flagged as restrictive and the permission to hide it
export function hideRestrictiveUiConfigItems(
  item: UiConfigPropsItem,
  hasHideRestrictiveItemsPermission: boolean
): boolean {
  return item.restrictive === true && hasHideRestrictiveItemsPermission
}
